import { API_ROOT_URL } from './config';
import { authHeaders } from './auth';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/deviceStorage';

export type ChildProfile = {
  id: number;
  user_id: number;
  nickname: string;
  age?: number | null;
  birth_year?: number | null;
  avatar_url?: string | null;
  created_at?: string;
};

const PROFILE_ID_KEY = 'profile_id';
const ACTIVE_PROFILE_KEY = 'active_child_profile';

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
  error?: boolean;
  errors?: Record<string, string[]>;
};

async function request<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(`${API_ROOT_URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    },
  });
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T>;

  if (!response.ok || json.error) {
    const firstError = Object.values(json.errors ?? {})[0]?.[0];
    throw new Error(firstError || json.message || `API ${response.status}: ${path}`);
  }

  return json.data as T;
}

export function getProfiles() {
  return request<ChildProfile[]>('/profiles');
}

export function createProfile(payload: { nickname: string; age: number; avatar_url?: string }) {
  return request<ChildProfile>('/profiles', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getSelectedProfileId() {
  const value = getStoredItem(PROFILE_ID_KEY);
  return value ? Number(value) : null;
}

export function getCachedActiveProfile() {
  const raw = getStoredItem(ACTIVE_PROFILE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as ChildProfile;
  } catch {
    return null;
  }
}

export async function saveSelectedProfile(profile: ChildProfile) {
  await setStoredItem(PROFILE_ID_KEY, String(profile.id));
  await setStoredItem(ACTIVE_PROFILE_KEY, JSON.stringify(profile));
}

export async function saveSelectedProfileId(profileId: number) {
  await setStoredItem(PROFILE_ID_KEY, String(profileId));
}

export async function clearSelectedProfileId() {
  await removeStoredItem(PROFILE_ID_KEY);
  await removeStoredItem(ACTIVE_PROFILE_KEY);
}

export async function getActiveProfile() {
  const selectedId = getSelectedProfileId();
  const cachedProfile = getCachedActiveProfile();

  if (cachedProfile && (!selectedId || cachedProfile.id === selectedId)) {
    return cachedProfile;
  }

  const profiles = await getProfiles();

  if (!profiles.length) {
    return null;
  }

  const profile = profiles.find((item) => item.id === selectedId) ?? profiles[0];
  await saveSelectedProfile(profile);
  return profile;
}
